<template>
    <modal
        id="unsubscribe-newsletter-modal"
        :centered="true"
        :title="$t('shop.unsubscribeNewsletterTitle')"
        :show="modal.show"
        :close="modal.close"
        :closeable="modal.closeable"
        @modal-invisible="successfulCompleted = modal.show = modal.close = false"
        :tighter-close-button="true"
        size="modal-lg"
    >
        <template v-if="!successfulCompleted">
            <loading-animation
                :visible="isOngoing"
                :always-show="false"
                preloader-container-id=""
                status-container-id=""
                :loading-text="$t('shop.unsubscribeNewsletterLoading')"
            ></loading-animation>
            <div class="modal-body">
                <div class="form-group">
                    <label for="unsubscribeNewsletterEmail" v-html="$t('shop.unsubscribeNewsletterEmail')"></label>
                    <input
                        type="email"
                        name="unsubscribeNewsletterEmail"
                        class="form-control"
                        id="unsubscribeNewsletterEmail"
                        :placeholder="$t('shop.email_placeholder')"
                        :class="{'is-invalid': formErrors.email}"
                        v-model="form.email"
                        required
                    />
                    <div class="invalid-feedback">
                        <span class="d-block" v-for="error in formErrors.email" :key="error">
                            {{ error }}
                        </span>
                    </div>
                    <div
                        class="alert alert-danger mt-2"
                        v-if="this.showCustomErrorMessage"
                        v-html="$t(this.customErrorMessage)">
                    </div>
                </div>
            </div>
            <div class="modal-footer justify-content-center row">
                <button
                    type="button"
                    class="btn btn-primary col-6"
                    :disabled="isOngoing"
                    @click="unsubscribe()"
                    v-html="$t('shop.unsubscribeNewsletterModalButton')"
                ></button>
            </div>
        </template>
        <template v-else>
            <div class="modal-body">
                <div
                    class="alert alert-success"
                    v-html="$t(this.successMessage)">
                </div>
            </div>
        </template>
    </modal>
</template>

<script>

export default {
    name: 'UnsubscribeNewsletterModal',

    props: {
        customerEmail: {
            type: String,
            required: false,
            default: null,
        },
    },

    data() {
        return {
            modal: {
                show: false,
                close: false,
                closeable: true,
            },
            isOngoing: false,
            formErrors: {},
            customErrorMessage: null,
            showCustomErrorMessage: false,
            form: {
                email: this.customerEmail,
            },
            successfulCompleted: false,
            successMessage: null,
        };
    },

    watch: {
        /**
         * Watches the customer-email param to set it at the form if necessary.
         *
         * @returns {void}
         */
        customerEmail() {
            this.form.email = this.customerEmail;
        },
    },

    mounted() {

    },

    methods: {
        /**
         * Handle the visibility change of the modal.
         *
         * @returns {void}
         */
        showModal() {
            this.modal.closeable = true;
            this.modal.close = false;
            this.modal.show = true;
            this.showCustomErrorMessage = false;
            this.customErrorMessage = null;
        },

        /**
         * Handle the hiding from the visible modal.
         *
         * @returns {void}
         */
        hideModal() {
            if (!this.modal.show) {
                return;
            }

            this.$nextTick(() => {
                this.modal.close = true;
            });
        },

        unsubscribe() {
            this.$set(this, 'formErrors', {});
            const errors = [];
            this.showCustomErrorMessage = false;

            if (typeof this.form.email !== 'string' || this.form.email.trim() === '') {
                errors.push(this.$t('shop.unsubscribeNewsletterMissingEmail'));
            } else if ((this.form.email.match(/^[\w-.]+@([\w-]+\.)+[\w-]{1,63}$/g) || []).length === 0) {
                errors.push(this.$t('shop.unsubscribeNewsletterInvalidEmail'));
            }

            if (errors.length > 0) {
                this.$set(this, 'formErrors', {
                    email: errors,
                });

                return;
            }

            // Avoid the submitting of multiple requests.
            if (this.isOngoing) {
                return;
            }

            this.isOngoing = true;
            axios.post('/newsletter/confirm-unsubscription', {
                email: this.form.email,
                locale: this.$language,
            }).then((response) => {
                if (!response.data.success) {
                    this.$set(this, 'formErrors', response.data.formErrors);
                    return;
                }
                this.successfulCompleted = true;
                this.successMessage = 'shop.unsubscribeNewsletterUnsubscribed';
            }).catch((err) => {
                if (err.status === 429) {
                    this.customErrorMessage = 'shop.unsubscribeNewsletterRateLimitError';
                } else if (typeof err.status == 'undefined') {
                    this.successfulCompleted = true;
                    this.successMessage = 'shop.unsubscribeNewsletterAlreadyUnsubscribed';
                } else {
                    this.customErrorMessage = 'shop.unsubscribeNewsletterFormError';
                }
                this.showCustomErrorMessage = true;
            }).then(() => {
                this.isOngoing = false;
            });
        },
    },
};
</script>

<style lang="scss">
#unsubscribe-newsletter-modal {
    .modal-title {
        max-width: 100% !important;
    }
    .custom-err-msg-box {
        width: 100%;
        margin-top: 0.25rem;
        font-size: 80%;
        color: #dc3545;
    }
    .loader-container {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        z-index: 9999999;

        background: rgba(255, 255, 255, 0.8);
    }
    .loader-container .preloader .status {
        .spinner {
            position: relative;
            text-align: center;

            .spinner-inner {
                position: relative;
                margin: auto auto 1rem;

                .double-bounce1,
                .double-bounce2 {
                    position: absolute;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    border-radius: 50%;

                    background-color: #2f55d4;
                    opacity: 0.6;

                    -webkit-animation: sk-bounce 2s infinite ease-in-out;
                    animation: sk-bounce 2s infinite ease-in-out;
                }

                .double-bounce2 {
                    -webkit-animation-delay: -1s;
                    animation-delay: -1s;
                }

                width: 2.5rem;
                height: 2.5rem;
            }

            .spinner-text {
                font-weight: bolder;
            }
        }

        position: absolute;
        top: 50%;
        left: 0;
        right: 0;
        -webkit-transform: translateY(-50%);
        transform: translateY(-50%);
    }
}
</style>
